import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import axios from 'axios';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { environment } from 'src/environments/environment';
import {
  BailiffDeliveryFeeTransferDialogComponent,
  BailiffDeliveryFeeTransferDialogData,
} from '../bailiff-delivery-fee-transfer-dialog/bailiff-delivery-fee-transfer-dialog.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { CurrencyPipe } from '@angular/common';

type DialogData = {
  old_address: string;
  delivery_fee: {
    amount: number;
    currency_iso: string;
  };
  transition_ids: {
    submit_address: string;
    request_delivery: string;
  };
  client_uses_credits: boolean;
  client_insufficient_credits: boolean;
  payee_case_reference_id: string;
  bailiff_delivery_allowed: boolean;
};

@Component({
  selector: 'app-request-bailiff-delivery-dialog',
  templateUrl: './request-bailiff-delivery-dialog.component.html',
  styleUrls: ['./request-bailiff-delivery-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    CurrencyPipe,
    TranslateModule,
  ],
})
export class RequestBailiffDeliveryDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<RequestBailiffDeliveryDialogComponent>>(MatDialogRef);
  private data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private workflowService = inject(WorkflowService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  dialogData?: DialogData;
  loading = false;
  loadingAddress = false;

  dialogMode: 'bailiff_delivery' | 'add_address' = 'add_address';
  readonly newAddressForm = this.fb.control('', {
    validators: Validators.required,
    nonNullable: true,
  });

  get isBailiffDeliveryMode(): boolean {
    return this.dialogMode === 'bailiff_delivery';
  }

  get isAddAddressMode(): boolean {
    return this.dialogMode === 'add_address';
  }

  setMode(mode: 'add_address' | 'bailiff_delivery'): void {
    this.dialogMode = mode;
  }

  get titleKey(): string {
    return this.isAddAddressMode
      ? 'WORKFLOW_DIALOGS.ADD_DELIVERY_ADDRESS.TITLE'
      : 'WORKFLOW_DIALOGS.REQUEST_BAILIFF_DELIVERY.TITLE';
  }

  get descriptionKey(): string {
    return this.isAddAddressMode
      ? 'WORKFLOW_DIALOGS.ADD_DELIVERY_ADDRESS.DESCRIPTION'
      : 'WORKFLOW_DIALOGS.REQUEST_BAILIFF_DELIVERY.DESCRIPTION';
  }

  get cancelKey(): string {
    return this.isAddAddressMode
      ? 'WORKFLOW_DIALOGS.ADD_DELIVERY_ADDRESS.CANCEL'
      : 'WORKFLOW_DIALOGS.REQUEST_BAILIFF_DELIVERY.CANCEL';
  }

  get showInsufficientCreditsWarning(): boolean {
    return !!(
      this.dialogData?.client_uses_credits &&
      this.dialogData?.client_insufficient_credits
    );
  }

  get showPayWithCreditsButton(): boolean {
    return !!(
      this.dialogData?.client_uses_credits &&
      !this.dialogData?.client_insufficient_credits
    );
  }

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;
  }

  readonly cancel = () => {
    this.dialogRef.close();
  };

  closeDialogAndGoToCredits(): void {
    this.dialogRef.close();
    const fee = this.dialogData?.delivery_fee;
    void this.router.navigate(['/user/credits'], {
      queryParams: {
        amount: fee?.amount ?? null,
        currency_iso: fee?.currency_iso ?? null,
      },
    });
  }

  async submitAddress(): Promise<void> {
    if (this.newAddressForm.invalid || !this.dialogData) {
      this.newAddressForm.markAsDirty();
      return;
    }
    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.dialogData.transition_ids.submit_address,
      params: {
        ...(this.data.extraParams ?? {}),
        message: this.newAddressForm.value ?? '',
      },
    };
    this.loadingAddress = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (err) {
      this.workflowService.showTransitionError(err);
    } finally {
      this.loadingAddress = false;
    }
  }

  async payByCard() {
    this.loading = true;
    try {
      await this.workflowService.runTransition(
        this.closeRequestBailiff('card'),
      );
      const url = `${environment.baseUrl}/api/case/${this.data.caseId}/bailiff-delivery/pay-with-card`;
      const res = await axios.get<string>(url);
      window.location.href = res.data;
    } catch (err) {
      this.loading = false;
      this.workflowService.showTransitionError(err);
    }
  }

  async payWithCredits() {
    const closeValue = this.closeRequestBailiff('credits');
    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (err) {
      this.workflowService.showTransitionError(err);
    } finally {
      this.loading = false;
    }
  }

  async payByTransfer() {
    this.loading = true;
    try {
      const { transition_at } = await this.workflowService.runTransition(
        this.closeRequestBailiff('transfer'),
      );
      const url = `${environment.baseUrl}/api/case/${this.data.caseId}/bailiff-delivery/transfer-instructions`;
      const res = await axios.get<BailiffDeliveryFeeTransferDialogData>(url, {
        params: { transition_at },
      });
      this.dialogRef.close(undefined);
      this.dialog.open(BailiffDeliveryFeeTransferDialogComponent, {
        data: res.data,
        autoFocus: false,
      });
    } catch (err) {
      this.loading = false;
      this.workflowService.showTransitionError(err);
    }
  }

  private closeRequestBailiff(
    paymentMethod: 'card' | 'credits' | 'transfer',
  ): RunTransition {
    if (!this.dialogData) {
      throw new Error('Missing dialog data');
    }

    return {
      caseId: this.data.caseId,
      transitionId: this.dialogData.transition_ids.request_delivery,
      params: {
        ...(this.data.extraParams ?? {}),
        payment_method: paymentMethod,
      },
    };
  }
}
